<template>
  <div></div>
</template>

<script>
import { mapGetters } from 'vuex'
import EventBus from '@/eventBus'
import VueToastedUtils from '@/utils/vue-toasted.utils.js'
import utils from '@/util'
import { IMPORT_OPTIONS } from '@/constant/eventActions/ImportEventActions.js'

export default {
  data() {
    return {
      currentToast: null,
      title: 'Importing slides…',
    }
  },

  computed: {
    ...mapGetters('aiChatbotImportPptxPdf', [
      'isCurrentPresentationImportingSlides',
      'selectedImportOption',
      'importProgress',
      'canUseImportPipelineV2',
      'canUseImportLoadingRedesign',
    ]),

    shouldShowToaster() {
      // AHA2-171: with the redesigned notification on, ImportProgressNotification
      // owns the whole import UX — stay silent so the two never double up.
      if (this.canUseImportLoadingRedesign) return false
      const isImportOnly = this.selectedImportOption === IMPORT_OPTIONS.IMPORT_ONLY
      return this.isCurrentPresentationImportingSlides && (isImportOnly || this.isEditableImport)
    },
    // AHA-47757: the editable (PPTX → content-v2) import drives this same
    // toaster — same importing flag, same progress shape.
    isEditableImport() {
      return this.selectedImportOption === IMPORT_OPTIONS.IMPORT_EDITABLE
    },
    importErrorText() {
      return `${this.$i18nPresenter.t('Import failed')}.`
    },

    // Live status for a v2 import. Null means "say nothing new" — the toaster
    // keeps its generic title, which is what v1 and the cloud-reconcile path
    // get since neither reports progress.
    //
    // Gated on the same flag as the pipeline itself, so with it off this
    // component behaves exactly as it did before.
    progressText() {
      // The editable import always reports progress (it has no v1 variant), so
      // it does not wait on the pipeline-v2 flag.
      if (!this.canUseImportPipelineV2 && !this.isEditableImport) return null
      const progress = this.importProgress
      if (!progress) return null

      const slides = progress.slides || {}
      const captions = progress.captions || {}

      if (progress.phase === 'converting') {
        return this.$i18nPresenter.t('Preparing your file…')
      }
      if (progress.phase === 'importing') {
        // 'Importing slides…' already existed in the locale files; the rest were
        // added for this ticket. The ellipsis is the CHARACTER, not three dots —
        // vue-i18n looks the key up verbatim, and 'Importing slides...' misses.
        if (!slides.total) return this.$i18nPresenter.t('Importing slides…')
        return this.$i18nPresenter.t('Importing slide {done} of {total}', {
          done: slides.created || 0,
          total: slides.total,
        })
      }
      // The slides already exist and are editable by now — this phase is only
      // filling in descriptions, so the copy should not read as "still importing".
      if (progress.phase === 'captioning') {
        if (!captions.total) return this.$i18nPresenter.t('Adding descriptions…')
        return this.$i18nPresenter.t('Adding descriptions ({done} of {total})', {
          done: captions.done || 0,
          total: captions.total,
        })
      }
      return null
    },
  },

  watch: {
    shouldShowToaster(newValue) {
      if (!newValue) return

      this.showImportingToaster()
    },

    progressText(text) {
      this.updateToasterText(text)
    },
  },

  mounted() {
    EventBus.$on('import: hide_is_importing_toaster', () => {
      this.hideCurrentToaster()
    })
    // Both carry the presentation they belong to. An import that outlives the
    // page it started from finishes while the user is on a different deck, and
    // an unscoped listener would show its result over whatever is happening
    // here — including over a running import's progress toaster.
    //
    // No payload means an older emitter that predates the stamp; those are
    // still honoured so nothing silently stops showing.
    EventBus.$on('import: show_import_status_success', (payload) => {
      if (!this.isForThisPresentation(payload)) return
      this.showSuccessToaster(payload)
    })
    EventBus.$on('import: show_import_status_error', (payload) => {
      if (!this.isForThisPresentation(payload)) return
      this.showErrorToaster(payload)
    })

    if (this.shouldShowToaster) {
      this.showImportingToaster()
    }
  },

  destroyed() {
    EventBus.$off('import: hide_is_importing_toaster')
    EventBus.$off('import: show_import_status_success')
    EventBus.$off('import: show_import_status_error')
  },

  methods: {
    isForThisPresentation(payload) {
      const target = payload?.presentationId
      if (target == null) return true
      return target === this.$store.state.presentation?.id
    },

    showImportingToaster() {
      this.currentToast = VueToastedUtils.showToast(
        this.progressText || this.$i18nPresenter.t(this.title),
        {},
        {
          className: 'custom-toast__loading',
          hasReturnValue: true,
          action: {
            text: this.$i18nPresenter.t('Dismiss'),
            onClick: (e, toastObject) => {
              toastObject.goAway(0)
            },
          },
        },
      )
    },

    // Rewrites just the message, leaving the icon and the Dismiss button alone.
    //
    // NOT toastObject.text(): vue-toasted implements that as
    // `el.innerHTML = text` (vue-toasted.js:719), which would wipe the action
    // button VueToastedUtils attached and any icon span it prepended. Updating
    // the text node in place is the only way to change the copy without
    // rebuilding the toast — and rebuilding it would flicker on every tick.
    updateToasterText(text) {
      if (!text) return
      const el = this.currentToast?.el
      if (!el) return
      const textNode = Array.from(el.childNodes).find(
        (node) => node.nodeType === Node.TEXT_NODE,
      )
      if (textNode) {
        textNode.nodeValue = ` ${text}`
      }
    },

    hideCurrentToaster() {
      if (this.currentToast) {
        this.$toasted.clear()
      }
    },

    // Re-checked AFTER the sleep, not just before it. This component lives in
    // App.vue, so it is one instance for the whole app rather than one per
    // deck: a user who switches decks inside the 500ms window would otherwise
    // see the old deck's terminal toast land on the new one.
    async showSuccessToaster(payload) {
      // AHA2-171: the redesigned notification renders its own Complete state.
      if (this.canUseImportLoadingRedesign) return
      this.hideCurrentToaster()
      await utils.sleep(500)
      if (!this.isForThisPresentation(payload)) return
      this.currentToast = VueToastedUtils.showToast(this.$i18nPresenter.t('Your slides are ready!'), {}, {
        className: 'custom-toast__success',
        hasReturnValue: true,
        duration: 3000,
      })
    },

    async showErrorToaster(payload) {
      // AHA2-171: error handling for the redesigned flow lives in its own path;
      // §8.1 (partial/hard failure) is not yet designed — see PR notes.
      if (this.canUseImportLoadingRedesign) return
      this.hideCurrentToaster()
      await utils.sleep(500)
      if (!this.isForThisPresentation(payload)) return
      // AHA-47757: an emitter may carry its own message (e.g. the editable
      // import pointing at the image path as a fallback); older emitters
      // without one keep the generic text.
      this.currentToast = VueToastedUtils.showToast(payload?.message || this.importErrorText, {}, {
        className: 'custom-toast__error',
        hasReturnValue: true,
        // A custom message carries an actionable suggestion — give it long
        // enough to actually be read.
        duration: payload?.message ? 6000 : 3000,
      })
    },
  },
}
</script>
